<script lang="ts" setup>import { computed as _computed } from 'vue';

import type { PurchasedOrder, PurchasedOrderGift } from '@consumer/types/serializers'
import { orders } from '@consumer/api'
import { asyncOpenModal } from '@consumer/services/modals'
import {
  faEnvelope,
  faMessageLines,
  faPrint,
  faMailboxFlagUp,
  faX,
} from '@fortawesome/pro-light-svg-icons'
import GSheetListButton from '@consumer/components/GSheetListButton.vue'

const props = defineProps<{
  orderGift: PurchasedOrderGift
  order: PurchasedOrder
}>()

// Support both parent-controlled (v-model:open) and self-managed (asyncOpenModal) modes
let open = (defineModel<boolean>('open', { default: true }))

const delivery = _computed(() => props.orderGift.delivery)

const estimatedDeliveryText = _computed(() => {
  if (!delivery.value?.estimatedRedeliveryDate) return null
  const date = new Date(delivery.value.estimatedRedeliveryDate)
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
})

const redeliveryOptions = _computed(() => {
  const options = [
    {
      value: 'email',
      label: 'Send by email',
      description: 'Deliver instantly to their inbox',
      icon: faEnvelope,
    },
    {
      value: 'text',
      label: 'Send by text',
      description: 'Send SMS directly to their phone',
      icon: faMessageLines,
    },
    {
      value: 'print',
      label: 'Download PDF',
      description: 'Print at home and hand deliver',
      icon: faPrint,
    },
  ]

  if (!props.orderGift.hasReplacementMailable) {
    options.unshift({
      value: 'mail',
      label: 'Redeliver by mail',
      description: estimatedDeliveryText.value
        ? `We'll ship a replacement for free.
      Estimated delivery by ${estimatedDeliveryText.value}`
        : 'We\'ll ship a replacement for free.',
      icon: faMailboxFlagUp,
    })
  }

  return options
})

function onClose () {
  open.value = false
}

async function selectOption (option: string) {
  if (option === 'mail') {
    open.value = false
    asyncOpenModal(import('./RedeliverByMailDialog.vue'), {
      orderGift: props.orderGift,
      order: props.order,
    }, true)
  }
  else if (option === 'email') {
    open.value = false
    asyncOpenModal(import('./RedeliverByEmailDialog.vue'), {
      orderGift: props.orderGift,
      order: props.order,
    }, true)
  }
  else if (option === 'text') {
    open.value = false
    asyncOpenModal(import('./RedeliverByTextDialog.vue'), {
      orderGift: props.orderGift,
      order: props.order,
    }, true)
  }
  else if (option === 'print') {
    await orders.downloadPdf({
      params: {
        ...props.order,
        giftId: props.orderGift.id,
      },
      download: true,
    })
    open.value = false
    asyncOpenModal(import('./RedeliverSuccessDialog.vue'), {}, true)
  }
}
</script>

<template>
  <GSheetDialog
    v-model:open="open"
    name="redeliver-options-dialog"
    useActionSheet
    class="redeliver-options-dialog"
    @close="onClose"
  >
    <template #header>
      <div class="flex flex-col gap-2 items-center justify-center">
        <div
          class="rounded-1 bg-red-pale flex items-center justify-center shadow-v2md mb-2"
          style="height: 44px; width: 44px;"
        >
          <V2Icon :icon="faX" class="text-red-base"/>
        </div>
        <div class="text-2xl">
          Gift not delivered?
        </div>
        <p class="text-v2-secondary text-sm">
          We're sorry your gift didn't arrive. Choose how you'd like to resend it - we'll take care of the rest.
        </p>
      </div>
    </template>

    <div class="flex flex-col">
      <div class="flex flex-col gap-3 px-6 pb-6">
        <GSheetListButton
          v-for="option in redeliveryOptions"
          :key="option.value"
          :icon="option.icon"
          :label="option.label"
          :description="option.description"
          @click="selectOption(option.value)"
        />
      </div>
    </div>
  </GSheetDialog>
</template>

<style>
.redeliver-options-dialog {
  &.g-dialog-panel {
    @media screen and (max-width: 1023px) {
      height: auto;
      max-height: calc(100svh - 34px);
    }

    @media screen and (min-width: 1024px) {
      width: 500px;
    }
  }
}
</style>
